/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, ChangeEventHandler, useCallback, useMemo, useState} from 'react';
import {makeStyles, TextField} from '@material-ui/core';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {PrimaryButton} from '../../../components/buttons/PrimaryButton';
import {TertiaryButton} from '../../../components/buttons/TertiaryButton';
import {dlsColor} from '../../../theme/theme';
import {DLSTheme, SavedMapViewType} from '../../../types/types';
import {BodyTypography} from '../../../typography/BodyTypography';

interface SavedMapViewsChooserInputType {
  savedMapViews?: SavedMapViewType[];
  mapViewToEdit?: SavedMapViewType
  onCancel?: Function;
  onSave?: Function
}

const textFieldStyleOverrides = (theme: DLSTheme) => ({
  root : {
    '&&' : {
      '&:hover' : {
        backgroundColor : 'transparent'
      }
    }
  },
  underline : {
    backgroundColor : 'transparent'
  },
  inputRoot : {
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  }
});

const useStyle = makeStyles(() => ({
  root : {
    display         : 'flex',
    flexDirection   : 'column',
    backgroundColor : dlsColor('white'),
    padding         : 12,
    boxShadow       : '6px 6px 10px rgba(0, 0, 0, 0.1)'
  },
  buttonContainer : {
    display       : 'flex',
    flexDirection : 'row',
    width         : '90%',
    padding       : 12,
    marginTop     : 12
  },
  textFieldRoot : {
    textTransform         : 'uppercase',
    fontWeight            : '500 !important' as any,
    '& #save-view-search' : {
      paddingLeft : '0 !important'
    }
  }
}));

const SavedMapViewsChooserInput = ({
  savedMapViews,
  mapViewToEdit,
  onCancel,
  onSave
}: SavedMapViewsChooserInputType) => {
  const classes                         = useStyle();
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [inputValue, setInputValue]     = useState(mapViewToEdit?.id || '');

  const descriptionList: string[] = useMemo(() => (
    translate('maps:mapViews.descriptionList').split(',')
  ), []);

  const handleChange = useCallback(debounce(({target:{value}}: ChangeEvent<HTMLInputElement>) => {
    setInputValue?.(value);
    const match = !!(savedMapViews.find(({id}) => value === id));
    setSaveDisabled(match && value !== mapViewToEdit?.id);
  }, 200), [savedMapViews, mapViewToEdit]);

  const onSubmit = useCallback(() => {
    onSave?.({inputValue});
  }, [inputValue, onSave]);

  const inputText = useMemo(() => (
    mapViewToEdit ? translate('maps:mapViews.inputPlaceholderEdit') : translate('maps:mapViews.inputPlaceholder')
  ), [mapViewToEdit]);

  return (
    <div className={classes.root}>
      <BodyTypography variant='body1'>
        {translate('maps:mapViews.description')}
      </BodyTypography>
      <BodyTypography variant='body1'>
        <ul>
          {descriptionList.map((descriptionItem) => (
            <li>
              {descriptionItem}
            </li>
          ))}
        </ul>
      </BodyTypography>
      <TextField variant='standard'
        error={saveDisabled}
        onChange={handleChange}
        defaultValue={mapViewToEdit?.id}
        InputLabelProps={{shrink: true}}
        styleOverrides={textFieldStyleOverrides}
        classes={{root: classes.textFieldRoot}}
        helperText={saveDisabled && inputValue && translate('maps:mapViews.duplicateName')}
        placeholder={inputText}
        label={inputText}
        type='search'
        id='save-view-search'
        autoFocus />
      <div className={classes.buttonContainer}>
        <TertiaryButton isRemoveTextpaddingRight
          fullWidth
          onClick={onCancel}>
          {translate('common:editors.shared.cancel')}
        </TertiaryButton>
        <PrimaryButton fullWidth type='submit' disabled={saveDisabled || !inputValue.length } onClick={onSubmit}>
          {translate('common:editors.shared.done')}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default SavedMapViewsChooserInput;
